import React from 'react';
import { Eye, ClipboardCheck, Edit2, MoreHorizontal } from 'lucide-react';
// Directo, no por el barril de ui: el barril arrastra react-router (SidebarMenu).
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../ui/dropdown-menu';
import { cn, fixMojibake } from '../../lib/utils';
import { campoDe } from '../../lib/rolInterno';
import { ESTATUS_STYLES, ESTATUS_LABELS, RESULTADO_STYLES } from './juegoEstatus';

// Un juego en un renglon, para la jornada del torneo.
//
// La tarjeta grande (JuegoCard) es de la agenda, donde cada juego es de un torneo y un
// dia distintos y hay que decirlo todo. Dentro del torneo, en una jornada, la fecha, el
// torneo y la sede ya estan en la cabecera y la hora es el bloque: repetirlos en cada
// tarjeta era lo que hacia que 10 juegos ocuparan tres pantallas (el dueno, 2026-09-09).
// Aqui queda lo que cambia de un juego al otro: campo, grupo, rival, marcador, estatus.
//
// Acciones con la misma jerarquia de siempre -- Asistencia manda, Ver acompana, Editar
// en el menu -- pero como iconos, porque en un renglon las palabras no caben.
const JuegoFila = ({ juego, onView, onEdit, onTakeAttendance }) => {
  const grupoNombre = fixMojibake(juego.grupo?.nombre_del_grupo || juego.grupos?.nombre_del_grupo || 'Grupo');
  const rivalNombre = fixMojibake(juego.equipo?.nombre || juego.equipo_nombre || 'Por definir');
  const campo = campoDe(juego);
  const isFinished = juego.estatus === 'terminado';
  const isCancelled = juego.estatus === 'cancelado';
  const canTakeAttendance = Boolean(onTakeAttendance) && !isFinished && !isCancelled;
  const hayMarcador = isFinished && juego.puntos_favor != null && juego.puntos_contra != null;

  return (
    <div className="flex flex-wrap items-center gap-x-3 py-1">
      {/* El campo primero: es la columna del rol y lo que se busca al llegar a la cancha. */}
      <span className="w-16 shrink-0 truncate text-xs tabular-nums text-muted-foreground">
        {campo || <span className="italic">Sin campo</span>}
      </span>

      <button
        type="button"
        onClick={onView ? () => onView(juego) : undefined}
        className="flex min-w-0 flex-1 basis-40 items-center gap-1.5 text-left text-sm"
      >
        <span className="truncate font-medium">{grupoNombre}</span>
        <span className="shrink-0 text-xs text-muted-foreground">vs</span>
        <span className="truncate">{rivalNombre}</span>
        {juego.condicion === 'visita' && (
          <Badge variant="outline" className="shrink-0 text-[10px] font-normal">Visita</Badge>
        )}
      </button>

      <div className="flex items-center gap-1.5">
        {hayMarcador && (
          <span className={cn('rounded border px-1.5 text-sm font-bold tabular-nums', RESULTADO_STYLES[juego.resultado])}>
            {juego.puntos_favor} - {juego.puntos_contra}
          </span>
        )}
        <Badge variant="outline" className={cn('text-[10px]', ESTATUS_STYLES[juego.estatus] || ESTATUS_STYLES.programado)}>
          {ESTATUS_LABELS[juego.estatus] || juego.estatus}
        </Badge>
      </div>

      <div className="ml-auto flex items-center gap-1">
        {canTakeAttendance && (
          <Button size="sm" className="h-7 w-7 p-0" title="Tomar asistencia" aria-label="Tomar asistencia" onClick={() => onTakeAttendance(juego)}>
            <ClipboardCheck className="h-4 w-4" />
          </Button>
        )}
        {onView && (
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Ver juego" aria-label="Ver juego" onClick={() => onView(juego)}>
            <Eye className="h-4 w-4" />
          </Button>
        )}
        {onEdit && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Más acciones" aria-label="Más acciones">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit(juego)}>
                <Edit2 className="h-4 w-4" />
                Editar juego
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
};

export default JuegoFila;
